import React, { useCallback, useId, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { MediaType, MediaData, LabelValueItem } from "@/model";
import { getLabelFromValue } from "@/util/typeUtil";
import { isValidParagraphUrl } from "@/util/helper/paragraphHelper";
import { useUploadedFiles, useSyncForm } from "@/hook";
import { DropdownButton, DropdownItems, DropdownItem, DropdownGroup, TextInput, TextInputRef } from "@/component/input";

import { ParagraphFileInput } from ".";


type MediaOption = MediaType | "none";

const refineMediaType = (mediaType?: string) => {
    if (["image", "image_square", "video", "audio"].includes(mediaType ?? "")) {
        return mediaType as MediaType;
    }
    return undefined;
};

const MEDIA_TYPE_OPTIONS: LabelValueItem<MediaOption>[] = [
    { label: "None", value: "none" },
    { label: "Image", value: "image" },
    { label: "Image Square", value: "image_square" },
    { label: "Video", value: "video" },
    { label: "Audio", value: "audio" }
];

interface MediaInputProps {
    data?: MediaData;
    required?: boolean;
    useImageSquare?: boolean;
    onChange: (value: MediaData) => void;
}

const MediaInput: React.FC<MediaInputProps> = (props) => {
    const { data, required = true, useImageSquare = false, onChange } = props;

    const intl = useIntl();
    const urlErrorId = useId();
    const { add, remove } = useUploadedFiles();

    const storedData = useRef<MediaData>({});
    const urlInputRef = useRef<TextInputRef>(null);

    const onChangeUrl = (url: string) => {
        const img = new (window as any).Image();
    
        img.addEventListener("load", () => {
            onChange({
                media_type: data!.media_type,
                media_url: url,
                media_width: img.width,
                media_height: img.height
            });
        });

        img.addEventListener("error", () => {
            onChange({
                media_type: data!.media_type,
                media_url: url
            });
        });
        img.src = url;
    };

    const onChangeMediaType = (selection: MediaOption) => {
        if (selection === "none") {
            storedData.current = { ...data };
            onChange({});
            return; 
        }
        if (!data?.media_type) {
            onChange({
                ...storedData.current,
                media_type: selection
            });
            return;
        }
        onChange({
            ...data,
            media_type: selection
        });
    };

    const onLoad = (file: File, url: string, img: HTMLImageElement) => {
        if (data?.media_url) {
            remove(data.media_url);
        }
        add(file, url);

        onChange({
            media_type: data!.media_type,
            media_url: url,
            file_size: file.size,
            media_width: img.width,
            media_height: img.height
        });
        urlInputRef.current?.set(url);
    };
    const onError = (file: File, url: string) => {
        if (data?.media_url) {
            remove(data.media_url);
        }
        add(file, url);

        onChange({
            media_type: data!.media_type,
            media_url: url
        });
        urlInputRef.current?.set(url);
    };

    const mediaTypeOptions = useMemo(() => {
        return MEDIA_TYPE_OPTIONS.filter(option => useImageSquare || option.value !== "image_square");
    }, [useImageSquare]);

    const urlInputInvalid = useMemo(() => {
        if (!required && !data?.media_url && !data?.media_type) {
            return false;
        }
        return !isValidParagraphUrl(data?.media_url ?? "");
    }, [data?.media_url, data?.media_type, required]);

    const syncFromProps = useCallback(() => {
        if (!data?.media_url || !urlInputRef.current) {
            return;
        }
        urlInputRef.current.set(data.media_url);
    }, [data?.media_url]);

    useSyncForm(syncFromProps);

    return (
        <div data-testid="paragraph-media-input"
             className="flex flex-col gap-2"
        >
            {!!data?.media_url && !isValidParagraphUrl(data?.media_url ?? "") &&
                <span id={urlErrorId}
                      className="text-invalid"
                >
                    {intl.formatMessage({ id: "i04077" })}
                </span>
            }
            <div className="flex gap-1 items-center">
                <DropdownGroup sm>{({ open, close }) => (<>
                    <DropdownButton open={open}
                                    close={close}
                                    className={classNames(
                                        "h-full",
                                        required && !data?.media_type && "input-invalid"
                                    )}
                    >
                        {getLabelFromValue(mediaTypeOptions, refineMediaType(data?.media_type) ?? "none")}
                    </DropdownButton>
                    <DropdownItems>
                        {mediaTypeOptions.map((option) => (
                            <DropdownItem key={option.value}
                                          onClick={() => onChangeMediaType(option.value)}
                            >
                                {option.label}
                            </DropdownItem>
                        ))}
                    </DropdownItems>
                </>)}</DropdownGroup>
                <TextInput ref={urlInputRef}
                           defaultValue={data?.media_url ?? ""}
                           required={required}
                           invalid={urlInputInvalid}
                           aria-errormessage={urlInputInvalid ? urlErrorId : undefined}
                           disabled={!data?.media_type}
                           placeholder={intl.formatMessage({ id: "i04036" })}
                           onChange={e => onChangeUrl(e.target.value)}
                />
                <ParagraphFileInput disabled={!data?.media_type}
                                    extensionType={refineMediaType(data?.media_type)}
                                    onLoad={onLoad}
                                    onError={onError}
                />
            </div>
        </div>
    );
};

export default MediaInput;
